import type * as filesApi from '../api/files';
import type { FileResponse } from '../types';

export const DEFAULT_MULTIPART_THRESHOLD_BYTES = 100 * 1024 * 1024; // 100MB
export const MIN_MULTIPART_PART_SIZE_BYTES = 5 * 1024 * 1024; // 5MB (backend constraint)
export const MAX_MULTIPART_PARTS = 10_000;

export type MultipartUploadPart = {
  partNumber: number;
  start: number;
  end: number;
  size: number;
};

export function chooseMultipartPartSizeBytes(fileSizeBytes: number): number {
  const safeSize = Math.max(0, fileSizeBytes);
  const minToFitLimit = Math.ceil(safeSize / MAX_MULTIPART_PARTS);
  const preferred = 10 * 1024 * 1024; // 10MB default
  return Math.max(MIN_MULTIPART_PART_SIZE_BYTES, minToFitLimit, preferred);
}

export function planMultipartParts(params: {
  fileSizeBytes: number;
  partCount: number;
  partSizeBytes: number;
  lastPartSizeBytes: number;
}): MultipartUploadPart[] {
  const { fileSizeBytes, partCount, partSizeBytes, lastPartSizeBytes } = params;
  if (!Number.isFinite(fileSizeBytes) || fileSizeBytes <= 0) return [];
  if (!Number.isInteger(partCount) || partCount <= 0) return [];
  if (!Number.isFinite(partSizeBytes) || partSizeBytes <= 0) return [];
  if (!Number.isFinite(lastPartSizeBytes) || lastPartSizeBytes <= 0) return [];

  const parts: MultipartUploadPart[] = [];
  let offset = 0;

  for (let index = 1; index <= partCount; index += 1) {
    const isLast = index === partCount;
    const size = isLast ? lastPartSizeBytes : partSizeBytes;
    const start = offset;
    const end = Math.min(fileSizeBytes, start + size);
    const actualSize = Math.max(0, end - start);

    parts.push({ partNumber: index, start, end, size: actualSize });
    offset = end;
  }

  return parts;
}

function isAbortError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const maybe = err as { name?: unknown };
  return maybe.name === 'AbortError';
}

function isNonRetryableStatusMessage(message: string): boolean {
  // uploadToPresignedURL currently throws: `Upload failed with status ${xhr.status}`
  return /status\s+(400|401|403|404)\b/.test(message);
}

function shouldRetryUploadError(err: unknown): boolean {
  if (isAbortError(err)) return false;
  if (err instanceof Error && isNonRetryableStatusMessage(err.message)) return false;
  return true;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function uploadMultipartFile(params: {
  file: File;
  initiate: (req: filesApi.InitiateMultipartUploadRequest) => Promise<filesApi.InitiateMultipartUploadResponse>;
  getPartUrl: (fileId: string, partNumber: number) => Promise<filesApi.PartUploadURLResponse>;
  uploadToPresignedUrl: (
    url: string,
    file: Blob,
    onProgress?: (progress: number) => void,
    signal?: AbortSignal
  ) => Promise<string>;
  complete: (fileId: string, req: filesApi.CompleteMultipartUploadRequest) => Promise<void>;
  confirm: (fileId: string) => Promise<FileResponse>;
  abort: (fileId: string) => Promise<void>;
  request: Omit<filesApi.InitiateMultipartUploadRequest, 'part_size' | 'file_size'> & {
    file_size: number;
  };
  partSizeBytes: number;
  resume?: {
    fileId: string;
    uploadId: string;
    partSize: number;
    partCount: number;
    lastPartSize: number;
    completedParts: Record<number, string>;
    needsConfirm?: boolean;
  };
  onInitiated?: (init: filesApi.InitiateMultipartUploadResponse) => void;
  onPartETag?: (partNumber: number, etag: string) => void;
  onNeedsConfirm?: (fileId: string) => void;
  onCompleted?: (fileId: string) => void;
  onProgress?: (progressPercent: number) => void;
  signal?: AbortSignal;
  concurrency?: number;
  maxAttemptsPerPart?: number;
}): Promise<FileResponse> {
  const {
    file,
    initiate,
    getPartUrl,
    uploadToPresignedUrl,
    complete,
    confirm,
    abort,
    request,
    partSizeBytes,
    resume,
    onInitiated,
    onPartETag,
    onNeedsConfirm,
    onCompleted,
    onProgress,
    signal,
    concurrency = 3,
    maxAttemptsPerPart = 3,
  } = params;

  const initResp = resume
    ? {
        file_id: resume.fileId,
        upload_id: resume.uploadId,
        part_size: resume.partSize,
        part_count: resume.partCount,
        last_part_size: resume.lastPartSize,
      }
    : await initiate({
        ...request,
        file_size: file.size,
        part_size: partSizeBytes,
      });

  if (!resume) {
    onInitiated?.(initResp);
  }

  const parts = planMultipartParts({
    fileSizeBytes: file.size,
    partCount: initResp.part_count,
    partSizeBytes: initResp.part_size,
    lastPartSizeBytes: initResp.last_part_size,
  });

  if (parts.length === 0) {
    throw new Error('Multipart upload could not be planned');
  }

  if (resume?.needsConfirm) {
    // Upload was already completed server-side; try confirming again.
    for (let attempt = 1; attempt <= 3; attempt += 1) {
      try {
        const confirmed = await confirm(initResp.file_id);
        onCompleted?.(initResp.file_id);
        return confirmed;
      } catch (err) {
        if (signal?.aborted || isAbortError(err)) {
          throw err;
        }
        if (attempt >= 3) throw err;
        await sleep(250 * attempt);
      }
    }
  }

  const loadedByPart = new Map<number, number>();
  const setLoaded = (partNumber: number, loadedBytes: number) => {
    loadedByPart.set(partNumber, Math.max(0, loadedBytes));
    const totalLoaded = Array.from(loadedByPart.values()).reduce((acc, v) => acc + v, 0);
    const progressPercent = file.size > 0 ? (totalLoaded / file.size) * 100 : 100;
    onProgress?.(Math.max(0, Math.min(100, progressPercent)));
  };

  const etagsByPart = new Map<number, string>();
  if (resume?.completedParts) {
    for (const [k, v] of Object.entries(resume.completedParts)) {
      const partNumber = Number(k);
      if (!Number.isInteger(partNumber) || partNumber <= 0) continue;
      if (typeof v !== 'string' || !v) continue;
      etagsByPart.set(partNumber, v);
    }
  }
  const queue = [...parts];
  let aborted = false;
  let completed = false;

  const worker = async () => {
    while (queue.length > 0) {
      if (signal?.aborted) {
        aborted = true;
        throw new DOMException('Aborted', 'AbortError');
      }

      const part = queue.shift();
      if (!part) return;

      if (etagsByPart.has(part.partNumber)) {
        setLoaded(part.partNumber, part.size);
        continue;
      }

      let attempt = 0;
      // Retry with backoff (best effort).
      for (;;) {
        attempt += 1;
        try {
          const presign = await getPartUrl(initResp.file_id, part.partNumber);
          const blob = file.slice(part.start, part.end, file.type);
          const etag = await uploadToPresignedUrl(
            presign.upload_url,
            blob,
            (p) => setLoaded(part.partNumber, (p / 100) * part.size),
            signal
          );
          etagsByPart.set(part.partNumber, etag);
          onPartETag?.(part.partNumber, etag);
          setLoaded(part.partNumber, part.size);
          break;
        } catch (err) {
          if (signal?.aborted || isAbortError(err)) {
            aborted = true;
            throw err;
          }
          if (attempt >= maxAttemptsPerPart || !shouldRetryUploadError(err)) {
            throw err;
          }
          const backoffMs = 500 * Math.pow(2, attempt-1);
          await sleep(backoffMs);
        }
      }
    }
  };

  try {
    const workerCount = Math.max(1, Math.min(10, Math.floor(concurrency)));
    await Promise.all(Array.from({ length: workerCount }, () => worker()));

    const completedParts = Array.from(etagsByPart.entries())
      .sort(([a], [b]) => a - b)
      .map(([partNumber, etag]) => ({ part_number: partNumber, etag }));

    await complete(initResp.file_id, { parts: completedParts });
    completed = true;
    onNeedsConfirm?.(initResp.file_id);

    // Confirm can fail transiently (e.g. network blip). Retry a few times before surfacing error.
    for (let attempt = 1; attempt <= 3; attempt += 1) {
      try {
        const confirmed = await confirm(initResp.file_id);
        onCompleted?.(initResp.file_id);
        return confirmed;
      } catch (err) {
        if (signal?.aborted || isAbortError(err)) {
          aborted = true;
          throw err;
        }
        if (attempt >= 3) {
          throw err;
        }
        await sleep(250 * attempt);
      }
    }

    throw new Error('Upload confirmation failed');
  } catch (err) {
    // Best-effort cleanup on explicit user cancel; for other failures, keep multipart state so the
    // next attempt can resume without reuploading completed parts.
    if (aborted) {
      try {
        await abort(initResp.file_id);
      } catch {
        // ignore
      }
      throw new DOMException('Aborted', 'AbortError');
    }

    // If we already completed the multipart upload server-side but failed to confirm, keep state.
    if (completed) {
      throw err;
    }

    // Non-retryable errors won't benefit from resume; abort to clean up.
    if (err instanceof Error && isNonRetryableStatusMessage(err.message)) {
      try {
        await abort(initResp.file_id);
      } catch {
        // ignore
      }
    }
    throw err;
  }
}
